import type { Vector3 } from "@babylonjs/core";

export type SynthSoundCue = "ui" | "coin" | "giggle" | "rustle" | "splash" | "slingshot" | "capture" | "miss";
export type SoundCue = SynthSoundCue | "toyol_escape_laugh" | "pontianak_scream";
export type AudioCategory = "ambience" | "sfx";

export interface AudioAssetDefinition {
  readonly path: string;
  readonly category: AudioCategory;
  readonly loop: boolean;
  readonly baseVolume: number;
  readonly cooldownMs?: number;
}

export const AUDIO_ASSETS = {
  sanggang: { path: "./assets/audio/Sanggang.wav", category: "ambience", loop: true, baseVolume: 0.3 },
  toyol_escape_laugh: { path: "./assets/audio/toyol_escape_laugh.mp3", category: "sfx", loop: false, baseVolume: 0.65, cooldownMs: 6500 },
  pontianak_scream: { path: "./assets/audio/virtual_vibes-woman-scream-sound-hd-379381.mp3", category: "sfx", loop: false, baseVolume: 0.82 },
} as const satisfies Record<string, AudioAssetDefinition>;

const SYNTH_SETTINGS: Readonly<Record<SynthSoundCue, readonly [number, number, OscillatorType]>> = {
  ui: [440, 0.05, "sine"], coin: [880, 0.12, "sine"], giggle: [620, 0.18, "triangle"],
  rustle: [115, 0.16, "sawtooth"], splash: [240, 0.22, "sine"], slingshot: [520, 0.11, "square"],
  capture: [740, 0.5, "triangle"], miss: [120, 0.12, "sine"],
};

export function computeEffectiveVolume(baseVolume: number, masterVolume: number, categoryVolume: number, muted: boolean): number {
  return muted ? 0 : Math.max(0, Math.min(1, baseVolume * masterVolume * categoryVolume));
}
export function computeDuckedAmbienceVolume(baseVolume:number,masterVolume:number,ambienceVolume:number,muted:boolean,duckMultiplier:number):number{return computeEffectiveVolume(baseVolume,masterVolume,ambienceVolume,muted)*Math.max(0,Math.min(1,duckMultiplier));}
export function equalPowerCrossfadeGains(progress:number):readonly[incoming:number,outgoing:number]{const t=Math.max(0,Math.min(1,progress));return[Math.sin(t*Math.PI/2),Math.cos(t*Math.PI/2)];}

export function computeSpatialAudio(source: Vector3, listener: Vector3, listenerForward: Vector3, maximumDistance = 18): { audible: boolean; volumeScale: number; pan: number } {
  const offset=source.subtract(listener);offset.y=0;const distance=offset.length();
  if(distance>maximumDistance)return{audible:false,volumeScale:0,pan:0};
  const direction=distance>0.001?offset.scale(1/distance):offset;
  const forwardLength=Math.hypot(listenerForward.x,listenerForward.z);const forwardX=forwardLength>0.001?listenerForward.x/forwardLength:0;const forwardZ=forwardLength>0.001?listenerForward.z/forwardLength:1;
  const rightX=forwardZ,rightZ=-forwardX;const pan=Math.max(-1,Math.min(1,direction.x*rightX+direction.z*rightZ));
  const volumeScale=distance<=2?1:Math.max(0.08,1-(distance-2)/Math.max(0.001,maximumDistance-2));
  return{audible:true,volumeScale,pan};
}

export class AudioCooldown {
  private nextAllowedAt = Number.NEGATIVE_INFINITY;
  constructor(readonly durationMs: number) {}
  canPlay(nowMs: number): boolean { return nowMs >= this.nextAllowedAt; }
  commit(nowMs: number): void { this.nextAllowedAt = nowMs + this.durationMs; }
  reset(): void { this.nextAllowedAt = Number.NEGATIVE_INFINITY; }
}

export class AudioManager {
  private context?: AudioContext;
  private ambienceBytes?: ArrayBuffer;
  private ambienceBuffer?: AudioBuffer;
  private ambienceOutput?: GainNode;
  private readonly ambienceSources = new Set<AudioBufferSourceNode>();
  private ambienceScheduler?: number;
  private ambienceStopTimer?: number;
  private nextAmbienceStart = 0;
  private ambienceGeneration = 0;
  private escapeLaugh?: HTMLAudioElement;
  private pontianakScream?: HTMLAudioElement;
  private ambienceRequested = false;
  private unlocked = false;
  private masterVolume = 0.7;
  private ambienceVolume = 1;
  private sfxVolume = 0.8;
  private muted = false;
  private ambienceDuckMultiplier = 1;
  private screamStatus: "idle" | "playing" | "paused" | "ended" | "muted" | "locked" | "failed" = "idle";
  private resumeScreamAfterPause = false;
  private readonly laughterCooldown = new AudioCooldown(AUDIO_ASSETS.toyol_escape_laugh.cooldownMs);

  setPreferences(masterVolume: number, muted: boolean, ambienceVolume = 1, sfxVolume = 0.8): void {
    this.masterVolume = this.clamp(masterVolume);
    this.ambienceVolume = this.clamp(ambienceVolume);
    this.sfxVolume = this.clamp(sfxVolume);
    this.muted = muted;
    this.applyMediaVolumes();
  }

  async unlock(): Promise<void> {
    try {
      if (!this.context) this.context = new AudioContext();
      if (this.context.state === "suspended") await this.context.resume();
      this.ensureAmbienceOutput();
      this.unlocked = true;
      this.ensureMedia();
      await this.ensureAmbienceBuffer();
      if (this.ambienceRequested) this.startAmbienceLoop();
    } catch (error) {
      console.warn("[Audio] Audio browser tidak dapat dibuka; permainan diteruskan tanpa audio.", error);
    }
  }

  async prepare():Promise<void>{
    this.ensureMedia();this.escapeLaugh?.load();this.pontianakScream?.load();
    if(!this.ambienceBytes){
      try{const response=await fetch(AUDIO_ASSETS.sanggang.path);if(!response.ok)throw new Error(`HTTP ${response.status}`);this.ambienceBytes=await response.arrayBuffer();}
      catch(error){console.warn("[Audio] Rakaman malam Sanggang tidak dapat dipramuat.",error);}
    }
  }

  play(cue: SoundCue): boolean {
    if (cue === "toyol_escape_laugh") return this.playEscapeLaugh();
    if (cue === "pontianak_scream") return this.playPontianakScream();
    return this.playSynth(cue,1,0);
  }

  playSpatial(cue: SynthSoundCue, source: Vector3, listener: Vector3, listenerForward: Vector3): boolean {
    const spatial=computeSpatialAudio(source,listener,listenerForward);return spatial.audible&&this.playSynth(cue,spatial.volumeScale,spatial.pan);
  }

  private playSynth(cue:SynthSoundCue,volumeScale:number,pan:number):boolean {
    if (this.muted || !this.context) return false;
    const [frequency, duration, type] = SYNTH_SETTINGS[cue];
    try {
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, this.context.currentTime);
      if (cue === "giggle") oscillator.frequency.exponentialRampToValueAtTime(980, this.context.currentTime + duration);
      gain.gain.setValueAtTime(0.0001, this.context.currentTime);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, this.masterVolume * this.sfxVolume * 0.12 * Math.max(0,Math.min(1,volumeScale))), this.context.currentTime + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, this.context.currentTime + duration);
      const panner=typeof this.context.createStereoPanner==="function"?this.context.createStereoPanner():undefined;if(panner){panner.pan.value=Math.max(-1,Math.min(1,pan));oscillator.connect(gain).connect(panner).connect(this.context.destination);}else oscillator.connect(gain).connect(this.context.destination);
      oscillator.start();
      oscillator.stop(this.context.currentTime + duration + 0.02);
      return true;
    } catch (error) {
      console.warn(`[Audio] Cue gagal: ${cue}`, error);
      return false;
    }
  }

  startAmbience(): void {
    this.ambienceRequested = true;
    if (this.unlocked) void this.ensureAmbienceBuffer().then(()=>this.startAmbienceLoop());
  }

  stopAmbience(fadeSeconds = 1): void {
    this.ambienceRequested = false;
    const context=this.context,output=this.ambienceOutput;if(!context||!output){this.stopAmbienceSources();return;}
    output.gain.cancelScheduledValues(context.currentTime);output.gain.setValueAtTime(output.gain.value,context.currentTime);output.gain.linearRampToValueAtTime(0,context.currentTime+Math.max(0,fadeSeconds));
    if(this.ambienceStopTimer!==undefined)window.clearTimeout(this.ambienceStopTimer);
    this.ambienceStopTimer=window.setTimeout(()=>{this.ambienceStopTimer=undefined;if(!this.ambienceRequested)this.stopAmbienceSources();},Math.max(0,fadeSeconds*1000)+30);
  }

  pause(): void {
    this.stopAmbienceSources();
    this.escapeLaugh?.pause();
    this.resumeScreamAfterPause=Boolean(this.pontianakScream&&!this.pontianakScream.paused&&!this.pontianakScream.ended);
    if(this.resumeScreamAfterPause){this.pontianakScream?.pause();this.screamStatus="paused";}
  }

  resume(): void {
    if (this.ambienceRequested && this.unlocked) this.startAmbienceLoop();
    if(this.resumeScreamAfterPause&&this.pontianakScream&&!this.muted){this.resumeScreamAfterPause=false;void this.pontianakScream.play().then(()=>{this.screamStatus="playing";}).catch((error:unknown)=>{this.screamStatus="failed";console.warn("[Audio] Jeritan Pontianak gagal disambung.",error);});}
  }

  stopSceneAudio(): void {
    this.setAmbienceDuckMultiplier(1);
    this.stopAmbience(1);
    if (this.escapeLaugh) { this.escapeLaugh.pause(); this.escapeLaugh.currentTime = 0; }
    if(this.pontianakScream){this.pontianakScream.pause();this.pontianakScream.currentTime=0;}
    this.resumeScreamAfterPause=false;this.screamStatus="idle";
  }

  setAmbienceDuckMultiplier(multiplier: number): void {
    this.ambienceDuckMultiplier = this.clamp(multiplier);
    this.applyAmbienceOutputVolume();
  }

  get ambienceDucking(): number { return this.ambienceDuckMultiplier; }
  get configuredAmbienceVolume():number{return this.ambienceVolume;}
  get effectiveAmbienceVolume():number{return this.ambienceTarget();}
  get pontianakScreamStatus():string{return this.screamStatus;}
  stopPontianakScream():void{if(this.pontianakScream){this.pontianakScream.pause();this.pontianakScream.currentTime=0;}this.resumeScreamAfterPause=false;if(this.screamStatus!=="failed")this.screamStatus="idle";}

  private ensureMedia(): void {
    if (typeof Audio === "undefined") return;
    if (!this.escapeLaugh) {
      this.escapeLaugh = new Audio(AUDIO_ASSETS.toyol_escape_laugh.path);
      this.escapeLaugh.loop = false;
      this.escapeLaugh.preload = "auto";
    }
    if(!this.pontianakScream){
      this.pontianakScream=new Audio(AUDIO_ASSETS.pontianak_scream.path);
      this.pontianakScream.loop=false;this.pontianakScream.preload="auto";
      this.pontianakScream.addEventListener("ended",()=>{this.screamStatus="ended";});
      this.pontianakScream.addEventListener("error",()=>{this.screamStatus="failed";});
    }
  }

  private playEscapeLaugh(nowMs = performance.now()): boolean {
    if (!this.unlocked || this.muted || !this.laughterCooldown.canPlay(nowMs)) return false;
    this.ensureMedia();
    if (!this.escapeLaugh || (!this.escapeLaugh.paused && !this.escapeLaugh.ended)) return false;
    try {
      this.escapeLaugh.currentTime = 0;
      this.escapeLaugh.volume = this.sfxTarget(AUDIO_ASSETS.toyol_escape_laugh.baseVolume);
      void this.escapeLaugh.play().catch((error: unknown) => console.warn("[Audio] Gelak Toyol gagal dimainkan.", error));
      this.laughterCooldown.commit(nowMs);
      return true;
    } catch (error) {
      console.warn("[Audio] Gelak Toyol tidak tersedia.", error);
      return false;
    }
  }

  private playPontianakScream():boolean{
    if(this.muted){this.screamStatus="muted";return false;}
    if(!this.unlocked){this.screamStatus="locked";return false;}
    this.ensureMedia();
    if(!this.pontianakScream){this.screamStatus="failed";return false;}
    try{
      this.resumeScreamAfterPause=false;this.pontianakScream.pause();this.pontianakScream.currentTime=0;
      this.pontianakScream.volume=this.sfxTarget(AUDIO_ASSETS.pontianak_scream.baseVolume);
      this.screamStatus="playing";
      void this.pontianakScream.play().catch((error:unknown)=>{this.screamStatus="failed";console.warn("[Audio] Jeritan Pontianak tidak dapat dimainkan; permainan diteruskan.",error);});
      return true;
    }catch(error){this.screamStatus="failed";console.warn("[Audio] Jeritan Pontianak tidak tersedia; permainan diteruskan.",error);return false;}
  }

  private applyMediaVolumes(): void {
    this.applyAmbienceOutputVolume();
    if (this.escapeLaugh) this.escapeLaugh.volume = this.sfxTarget(AUDIO_ASSETS.toyol_escape_laugh.baseVolume);
    if(this.pontianakScream)this.pontianakScream.volume=this.sfxTarget(AUDIO_ASSETS.pontianak_scream.baseVolume);
    if (this.muted) { this.escapeLaugh?.pause();this.pontianakScream?.pause(); }
    else if (this.ambienceRequested && this.unlocked) this.startAmbienceLoop();
  }

  private ambienceTarget(): number { return computeDuckedAmbienceVolume(AUDIO_ASSETS.sanggang.baseVolume, this.masterVolume, this.ambienceVolume, this.muted, this.ambienceDuckMultiplier); }
  private sfxTarget(baseVolume: number): number { return computeEffectiveVolume(baseVolume, this.masterVolume, this.sfxVolume, this.muted); }

  private ensureAmbienceOutput():void{if(!this.context||this.ambienceOutput)return;this.ambienceOutput=this.context.createGain();this.ambienceOutput.gain.value=0;this.ambienceOutput.connect(this.context.destination);}
  private async ensureAmbienceBuffer():Promise<void>{
    if(this.ambienceBuffer||!this.context)return;
    if(!this.ambienceBytes)await this.prepare();
    if(!this.ambienceBytes)return;
    try{this.ambienceBuffer=await this.context.decodeAudioData(this.ambienceBytes.slice(0));}
    catch(error){console.warn("[Audio] Rakaman malam Sanggang tidak dapat dinyahkod.",error);}
  }
  private startAmbienceLoop():void{
    const context=this.context,buffer=this.ambienceBuffer;if(!context||!buffer||!this.ambienceRequested||this.muted||this.ambienceSources.size>0)return;
    this.ensureAmbienceOutput();if(!this.ambienceOutput)return;
    if(this.ambienceStopTimer!==undefined){window.clearTimeout(this.ambienceStopTimer);this.ambienceStopTimer=undefined;}
    this.ambienceGeneration++;this.nextAmbienceStart=context.currentTime+.04;this.applyAmbienceOutputVolume(true);this.scheduleAmbienceAhead(true);
    this.ambienceScheduler=window.setInterval(()=>this.scheduleAmbienceAhead(false),1000);
  }
  private scheduleAmbienceAhead(first:boolean):void{
    const context=this.context,buffer=this.ambienceBuffer,output=this.ambienceOutput;if(!context||!buffer||!output||!this.ambienceRequested)return;
    const overlap=Math.max(1.5,Math.min(4,Math.min(2,buffer.duration*.22)));const interval=Math.max(.25,buffer.duration-overlap);let isFirst=first;
    while(this.nextAmbienceStart<context.currentTime+8){
      const source=context.createBufferSource(),gain=context.createGain(),start=this.nextAmbienceStart;source.buffer=buffer;source.connect(gain).connect(output);
      const curveSize=64,incoming=new Float32Array(curveSize),outgoing=new Float32Array(curveSize);for(let index=0;index<curveSize;index++){const[gainIn,gainOut]=equalPowerCrossfadeGains(index/(curveSize-1));incoming[index]=gainIn;outgoing[index]=gainOut;}
      if(isFirst)gain.gain.setValueAtTime(1,start);else gain.gain.setValueCurveAtTime(incoming,start,overlap);
      gain.gain.setValueCurveAtTime(outgoing,start+buffer.duration-overlap,overlap);source.start(start);source.stop(start+buffer.duration+.05);this.ambienceSources.add(source);source.onended=()=>{this.ambienceSources.delete(source);source.disconnect();gain.disconnect();};
      this.nextAmbienceStart+=interval;isFirst=false;
    }
  }
  private applyAmbienceOutputVolume(immediate=false):void{const context=this.context,output=this.ambienceOutput;if(!context||!output)return;const target=this.ambienceRequested?this.ambienceTarget():0;output.gain.cancelScheduledValues(context.currentTime);if(immediate)output.gain.setValueAtTime(target,context.currentTime);else output.gain.setTargetAtTime(target,context.currentTime,.035);}
  private stopAmbienceSources():void{this.ambienceGeneration++;if(this.ambienceScheduler!==undefined){window.clearInterval(this.ambienceScheduler);this.ambienceScheduler=undefined;}for(const source of this.ambienceSources){source.onended=null;try{source.stop();}catch{/* Already stopped. */}source.disconnect();}this.ambienceSources.clear();this.nextAmbienceStart=0;}

  private clamp(value: number): number { return Math.max(0, Math.min(1, value)); }
}
